import React, { useState, useEffect } from 'react';
import Table from '../../molecules/tablas/view';
import SearchInput from '../../atoms/SearchInput/view';
import { getReceivableSummary, getPayableSummary } from '../../../usecases/accounts-management-usecase';
import { getCookie } from '../../../utils/cookies';
import { EmptyArray } from '../../../domain/empty/model';
import ModalBtn from '../../molecules/modalButton/view';
import { ChevronUp, ChevronDown, Eye, Download } from 'lucide-react';
import { formatMoney } from '../../../utils/formating';
import ExcelJS from 'exceljs';
import OptionsMenu from '../../atoms/OptionsMenu/view';
import ButtonBasic from '../../atoms/buttonBasic/view';
import { lang } from '../../../utils/langs';

import AccountPaymentModal from '../AccountPaymentModal/view';
import './style.css';
import CustomBox from '../../atoms/container/container';

const TransactionsTable = ({ transactions }) => {
    if (!transactions || transactions.length === 0) {
        return (
            <div className="no-transactions">
                No hay transacciones registradas
            </div>
        );
    }

    return (
        <div className="table-wrapper">
            <table className="transaction-table">
                <thead className="transaction-table-head">
                    <tr>
                        <th>Fecha</th>
                        <th>Monto</th>
                        <th>Método</th>
                        <th>Referencia</th>
                    </tr>
                </thead>
                <tbody>
                    {transactions.map((t, i) => (
                        <tr key={i}>
                            <td>{new Date(t.date).toLocaleDateString()}</td>
                            <td className="text-success-bold">{formatMoney(t.amount)}</td>
                            <td>
                                <span className="status-badge-manual">
                                    {t.paymethod_name || 'Manual'}
                                </span>
                            </td>
                            <td className="text-muted-small">{t.reference || 'Abono manual'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const AccountDetailCard = ({ item, thirdName, thirdIdentification }) => {
    const totalAbonado = item.initial_amount - item.current_balance;
    const progress = (totalAbonado / item.initial_amount) * 100;

    return (
        <div className="account-detail-card">
            {/* Account Info Header */}
            <div className="detail-header-divider">
                <span className="detail-header-text">Información de la Cuenta</span>
                <div className="detail-header-line"></div>
            </div>

            <div className="summary-grid">
                <div className="summary-item-label">
                    <div>Cliente: <span className="summary-item-value">{thirdName}</span></div>
                    <div>Identificación: <span className="summary-item-value">{thirdIdentification}</span></div>
                </div>
                <div className="summary-item-label" style={{ textAlign: 'right' }}>
                    <div>Factura: <span className="summary-item-important">#{item.number}</span></div>
                    <div>Fecha de Vencimiento: <span className="summary-item-value">{new Date(item.due_date).toLocaleDateString()}</span></div>
                </div>
            </div>

            <hr className="land-divider" style={{ margin: '20px 0' }} />

            <div className="summary-grid" style={{ marginBottom: '20px' }}>
                <div>
                    <div className="summary-item-label">Monto Inicial:</div>
                    <div className="summary-item-important">{formatMoney(item.initial_amount)}</div>
                    <div className="summary-item-label" style={{ marginTop: '10px' }}>Saldo Actual:</div>
                    <div className="balance-red">{formatMoney(item.current_balance)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div className="summary-item-label">Total Abonado:</div>
                    <div className="balance-green">{formatMoney(totalAbonado)}</div>
                </div>
            </div>



            <div className="detail-header-divider" style={{ marginBottom: '15px' }}>
                <span className="detail-header-text" style={{ fontSize: '15px' }}>Historial de Abonos ({item.transactions?.length || 0})</span>
                <div className="detail-header-line" style={{ marginTop: '10px' }}></div>
            </div>

            <TransactionsTable transactions={item.transactions} />
        </div>
    );
};

const ProfileModal = ({ data, type, onPaymentSuccess }) => {
    const [details, setDetails] = useState(EmptyArray);
    const [expandedRow, setExpandedRow] = useState(null);

    const handleOpen = () => {
        setDetails(data || EmptyArray);
        setExpandedRow(null); // Reset expanded row when modal opens
    };

    useEffect(() => {
        if (data) {
            setDetails(data);
        }
    }, [data]);

    const toggleRow = (index) => {
        setExpandedRow(expandedRow === index ? null : index);
    };

    const handleExportExcel = async () => {
        if (!details || !details.details) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Detalle Cartera');

        // Main Header
        const titleRow = worksheet.addRow(["DETALLE DE CARTERA - " + details.third_name.toUpperCase()]);
        titleRow.getCell(1).font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
        titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A73E8' } };
        worksheet.mergeCells(`A${titleRow.number}:E${titleRow.number}`);

        worksheet.addRow(["Identificación:", details.third_identification]);
        worksheet.addRow(["Total Deuda:", details.total_debt]);
        worksheet.addRow([]); // Gap

        details.details.forEach(doc => {
            // Document Header Row
            const docHeaderRow = worksheet.addRow(["NÚMERO DOCUMENTO", "FECHA VENCIMIENTO", "MONTO INICIAL", "SALDO ACTUAL", "ESTADO"]);
            docHeaderRow.eachCell(cell => {
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } }; // Light Gray
            });

            worksheet.addRow([
                doc.number,
                new Date(doc.due_date).toLocaleDateString(),
                doc.initial_amount,
                doc.current_balance,
                doc.status === 'paid' ? 'PAGADO' : doc.status === 'partial' ? 'PARCIAL' : 'PENDIENTE'
            ]);

            if (doc.transactions && doc.transactions.length > 0) {
                // Transactions Header
                const transHeaderRow = worksheet.addRow(["", "TRANSACCIONES:", "FECHA", "MONTO", "MÉTODO PAGO", "REFERENCIA", "TIPO"]);
                transHeaderRow.eachCell(cell => {
                    cell.font = { italic: true, bold: true };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } }; // White Smoke
                });

                doc.transactions.forEach(t => {
                    worksheet.addRow([
                        "",
                        "",
                        new Date(t.date).toLocaleString(),
                        t.amount,
                        t.paymethod_name,
                        t.reference,
                        t.type
                    ]);
                });
            }
            worksheet.addRow([]); // Blank row between docs
        });

        worksheet.columns.forEach(col => { col.width = 22; });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const fileName = `cartera_${details.third_name.replace(/\s+/g, '_')}.xlsx`;

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    };

    return (
        <ModalBtn
            id={"profile" + (data.details && data.details[0].sell_id ? data.details[0].sell_id : data.details[0].buy_id)}
            type='action'
            title={<Eye size={20} />}
            onClick={handleOpen}
        >
            <div className="modal-actions-header">
                <OptionsMenu>
                    <ButtonBasic onClick={handleExportExcel} type='option'>
                        <Download size={18} /> {lang('es', 'exportar_excel')}
                    </ButtonBasic>
                </OptionsMenu>
            </div>
            <div className='table-wrapper'>
                <table className="table accordion-table">
                    <thead>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">NÚMERO</th>
                            <th scope="col">FECHA DE VENCIMIENTO</th>
                            <th scope="col">MONTO INICIAL</th>
                            <th scope="col">SALDO ACTUAL</th>
                            <th scope="col">ESTADO</th>
                            <th scope="col" style={{ textAlign: 'center' }}>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody className='table-body'>
                        {details.details?.map((item, idx) => (
                            <React.Fragment key={idx}>
                                <tr
                                    className="accordion-row"
                                >
                                    <td scope="row" style={{ width: '40px', textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleRow(idx)}>
                                        {expandedRow === idx ?
                                            <ChevronUp size={20} className="text-muted-small" /> :
                                            <ChevronDown size={20} className="text-muted-small" />
                                        }
                                    </td>
                                    <td scope="row">{item.number}</td>
                                    <td scope="row">{new Date(item.due_date).toLocaleDateString('es-ES')}</td>
                                    <td scope="row">{formatMoney(item.initial_amount)}</td>
                                    <td scope="row">{formatMoney(item.current_balance)}</td>
                                    <td scope="row">
                                        <span className={`badge-status ${item.status === 'paid' ? 'badge-paid' : item.status === 'partial' ? 'badge-partial' : 'badge-pending'}`}>
                                            {item.status === 'paid' ? 'Pagado' : item.status === 'partial' ? 'Parcial' : 'Pendiente'}
                                        </span>
                                    </td>
                                    <td scope="row" style={{ textAlign: 'center' }}>
                                        {item.status !== 'paid' && (
                                            <AccountPaymentModal
                                                id={idx}
                                                account={item}
                                                type={type}
                                                onPaymentSuccess={onPaymentSuccess}
                                                onClose={() => null}
                                            />
                                        )}
                                    </td>
                                </tr>
                                {expandedRow === idx && (
                                    <tr className="expanded-row">
                                        <td colSpan="7" className="expanded-row-cell">
                                            <AccountDetailCard
                                                item={item}
                                                thirdName={details.third_name}
                                                thirdIdentification={details.third_identification}
                                            />
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </ModalBtn>
    );
};

const AccountsSummaryTable = ({ type }) => {
    const [data, setData] = useState({
        total: 0,
        page: 1,
        page_size: 10,
        data: [],
    });
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchData(1, search);
    }, [type]);

    const fetchData = async (pageNum = 1, searchQuery = search, pageSize = data.page_size) => {
        setLoading(true);
        const uid = getCookie('uid');
        const cid = getCookie('cid');
        try {
            let result;
            if (type === 'receivable') {
                result = await getReceivableSummary(uid, cid, pageNum, pageSize, searchQuery);
            } else {
                result = await getPayableSummary(uid, cid, pageNum, pageSize, searchQuery);
            }
            if (!result || !result.data || result.data.length === 0) {
                setData({
                    total: 0,
                    page: 1,
                    page_size: pageSize,
                    data: EmptyArray,
                });
                return;
            }
            setData(result);
        } catch (error) {
            console.error(error);
            setData(prev => ({ ...prev, data: EmptyArray }));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        const query = e.target.value;
        setSearch(query);
        fetchData(1, query, data.page_size);
    };

    const handlePageChange = (pageNum, pageSize = data.page_size) => {
        fetchData(pageNum, search, pageSize);
    };

    const headers = [
        "third_name",
        "third_identification",
        "total_debt"
    ];

    const formats = {
        "total_debt": { type: "money" }
    };

    return (
        <CustomBox style={{ maxHeight: '90%' }}>
            <div className="accounts-summary-header">
                <SearchInput
                    placeholder="Buscar por nombre o identificación..."
                    onChange={handleSearch}
                />
            </div>

            <Table
                id={type === 'receivable' ? "receivable-summary" : "payable-summary"}
                paginatedData={data}
                getFunction={handlePageChange}
                headersList={headers}
                formats={formats}
                excludeList={["third_id", "details", 'count_docs']}
                allowExport={true}
                exportFileName={type === 'receivable' ? "cuentas-por-cobrar" : "cuentas-por-pagar"}
                allowPageSizeControl={true}
                loading={loading}
            >
                <ProfileModal type={type} onPaymentSuccess={() => fetchData(data.page, search, data.page_size)} />
            </Table>
        </CustomBox>
    );
};

export default AccountsSummaryTable;
